import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ArrowRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { SITE } from "@/lib/siteData";
import { PORTFOLIO, PORTFOLIO_CATEGORIES, CATEGORY_LABEL } from "@/lib/portfolioData";
import { YouTubeEmbed } from "@/components/site/YouTubeEmbed";

const getThumb = (id, isShort) =>
  isShort
    ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg`
    : `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;

// Homepage tabs — all 14 portfolio categories, with "featured" as the default.
// Categories with zero items are auto-skipped so the tab row stays clean.
const HOME_TABS = [
  { id: "featured", label: "Featured" },
  ...PORTFOLIO_CATEGORIES.filter((c) =>
    PORTFOLIO.some(
      (p) =>
        p.category === c.slug ||
        (Array.isArray(p.extraCategories) && p.extraCategories.includes(c.slug)),
    ),
  ).map((c) => ({ id: c.slug, label: c.label })),
];

// Cap items rendered on the homepage so the section stays snappy.
const HOME_MAX = 8;

export const Portfolio = () => {
  const [active, setActive] = useState("featured");
  const [openId, setOpenId] = useState(null);

  const visible = useMemo(() => {
    const pool =
      active === "featured"
        ? PORTFOLIO.filter((p) => p.featured)
        : PORTFOLIO.filter(
            (p) =>
              p.category === active ||
              (Array.isArray(p.extraCategories) && p.extraCategories.includes(active)),
          );
    return pool.slice(0, HOME_MAX);
  }, [active]);

  const openItem = PORTFOLIO.find((v) => v.id === openId);

  return (
    <section id="portfolio" data-testid="featured-portfolio-section" className="section-pad relative bg-[hsl(225,22%,4%)]">
      <div className="container-site">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10 sm:mb-14">
          <div>
            <p className="kicker mb-3">Selected work</p>
            <h2 className="font-display uppercase text-foreground text-[clamp(2rem,5vw,4rem)] leading-[0.95]">
              The reel.
            </h2>
          </div>
          <a
            data-testid="portfolio-view-all-link"
            href={SITE.socials.youtube}
            target="_blank"
            rel="noreferrer noopener"
            className="text-sm uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors"
          >
            View full channel →
          </a>
        </div>

        <Tabs value={active} onValueChange={setActive} data-testid="portfolio-tabs">
          <TabsList className="bg-card border border-border rounded-full p-1 mb-8 sm:mb-12 inline-flex flex-wrap h-auto">
            {HOME_TABS.map((t) => (
              <TabsTrigger
                key={t.id}
                value={t.id}
                data-testid={`portfolio-tab-${t.id}`}
                className="rounded-full px-4 sm:px-5 py-2 text-xs uppercase tracking-[0.18em] text-muted-foreground data-[state=active]:bg-[hsl(var(--primary))] data-[state=active]:text-[hsl(var(--primary-foreground))] data-[state=active]:shadow-none"
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {HOME_TABS.map((t) => (
            <TabsContent key={t.id} value={t.id} className="mt-0">
              {visible.length === 0 ? (
                <p className="text-muted-foreground text-sm">No projects in this category yet.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                  {visible.map((item, i) => (
                    <motion.button
                      key={item.id}
                      data-testid={`portfolio-video-tile-${item.id}`}
                      onClick={() => setOpenId(item.id)}
                      initial={{ opacity: 0, y: 18 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 0.6, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                      className="group relative overflow-hidden rounded-xl border border-border bg-card text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
                    >
                      <AspectRatio ratio={item.isShort ? 9 / 16 : 16 / 9}>
                        <img
                          src={getThumb(item.youtubeId, item.isShort)}
                          alt={item.title}
                          className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                          loading="lazy"
                          onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${item.youtubeId}/hqdefault.jpg`; }}
                        />
                      </AspectRatio>
                      {/* Gradient overlay */}
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                      {/* Play badge */}
                      <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full bg-black/55 backdrop-blur-sm border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-white">
                        <Play className="h-3 w-3 fill-current" />
                        Watch
                      </div>
                      {item.isShort && (
                        <div className="absolute top-4 right-4 inline-flex items-center rounded-full bg-black/60 backdrop-blur-sm border border-white/20 px-2.5 py-1 text-[9px] uppercase tracking-[0.22em] text-white">
                          Reel
                        </div>
                      )}
                      {/* Hover play disc */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] shadow-[0_20px_50px_rgba(0,0,0,0.6)]">
                          <Play className="h-6 w-6 fill-current" />
                        </span>
                      </div>
                      {/* Meta */}
                      <div className="absolute inset-x-0 bottom-0 px-5 py-4">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
                          {CATEGORY_LABEL[item.category] || "Pie in the Sky Productions"}
                        </div>
                        <div className="mt-1 font-display text-xl sm:text-2xl uppercase text-white tracking-[0.02em] leading-tight line-clamp-2">
                          {item.title}
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>

        {/* See full portfolio CTA — routes to full 14-category filter page */}
        <div className="mt-10 sm:mt-12 flex justify-center">
          <Link
            to={active === "featured" ? "/portfolio" : `/portfolio?category=${active}`}
            data-testid="portfolio-see-full-cta"
            className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--primary))/0.5] bg-transparent px-6 py-3 text-xs uppercase tracking-[0.22em] text-foreground hover:bg-[hsl(var(--primary))] hover:text-[hsl(var(--primary-foreground))] hover:border-[hsl(var(--primary))] transition-colors"
          >
            {active === "featured"
              ? `See all ${PORTFOLIO.length} projects`
              : `See all ${CATEGORY_LABEL[active] || "portfolio"}`}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <Dialog open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent data-testid="portfolio-video-dialog" className="max-w-5xl w-[94vw] p-0 bg-black border-border overflow-hidden">
          <DialogTitle className="sr-only">{openItem?.title || "Portfolio video"}</DialogTitle>
          <div className="w-full">
            {openItem && (<YouTubeEmbed videoId={openItem.youtubeId} title={openItem.title} />)}
          </div>
          {openItem && (
            <div className="px-5 sm:px-7 py-5 border-t border-border bg-card">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
                {CATEGORY_LABEL[openItem.category]}
              </div>
              <div className="mt-1 font-display text-2xl uppercase text-foreground">{openItem.title}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
